import { ReactElement } from "react";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";

import { LS_AGENT_TICKETS_FILTERS, LS_AGENT_TICKETS_PAGE_VISIBLE_COLUMNS, fixedWidthColumnStyle } from "../../common/utils/common";
import { UserRoleEnum } from "../userRoles/UserRole";
import { inWorkStatusesIds } from "../ticketStatuses/TicketStatus";
import { convertDateStringToDate, convertDateToDateString } from "../../common/utils/dateHelper";
import UrgencyTag from "../urgencies/UrgencyTag";
import SlaProgressBar from "../tickets/SlaProgressBar";
import UrgencyDropdown from "../urgencies/UrgenciesDropdown";
import UsersDropdown from "../users/UsersDropdown";
import TicketStatusMultiSelect from "../ticketStatuses/TicketStatusMultiSelect";
import LocationsMultiSelect from "../locations/LocationsMultiSelect";
import TeamsMultiSelect from "../teams/TeamsMultiSelect";
import CategoryMultiselect from "../categories/CategoryMultiselect";
import { Ticket } from "./Ticket";

export interface ColumnDefProps {
  departmentId: string;
  agentsFilterTeamId?: string;
  grandParentCategoryIds?: string[];
  parentCategoryIds?: string[];
  availableCategoryIds?: string[];
}

export interface ColumnDef {
  header: string;
  column: (key: string) => ReactElement<Column>;
}

export const columns = ({
  departmentId,
  agentsFilterTeamId,
  grandParentCategoryIds,
  parentCategoryIds,
  availableCategoryIds,
}: ColumnDefProps): ColumnDef[] => [
  {
    header: "Termen",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(150)}
        header="Termen"
        field="slaPercent"
        body={(rowData: Ticket) => (
          <SlaProgressBar slaPercent={rowData.slaPercent} slaEndDatePretty={rowData.slaEndDatePretty}></SlaProgressBar>
        )}
      ></Column>
    ),
  },
  {
    header: "Prioritate",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(150)}
        className="text-center"
        header="Prioritate"
        field="urgency"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <UrgencyDropdown showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
        )}
        body={(rowData: Ticket) => <UrgencyTag value={rowData.urgency}></UrgencyTag>}
      ></Column>
    ),
  },
  {
    header: "Status",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(150)}
        className="text-center"
        header="Status"
        field="status"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TicketStatusMultiSelect
            showClear
            departmentId={departmentId}
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e)}
          />
        )}
        body={(rowData: Ticket) => <span>{rowData.statusType.name}</span>}
      ></Column>
    ),
  },
  {
    header: "Id",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(100)}
        header="Id"
        field="prettyId"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
      ></Column>
    ),
  },
  {
    header: "Titlu",
    column: (key: string) => (
      <Column
        key={key}
        style={{ minWidth: "200px" }}
        header="Titlu"
        field="subject"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        body={(rowData: Ticket) => <span className="font-semibold">{rowData.subject}</span>}
      ></Column>
    ),
  },
  {
    header: "Categorie primara",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Categorie primara"
        field="grandParentCategoryIds"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <CategoryMultiselect
            departmentId={departmentId}
            level={0}
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e)}
          />
        )}
        body={(rowData: Ticket) => <span>{rowData.grandParentCategory?.name}</span>}
      />
    ),
  },
  {
    header: "Categorie secundara",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Categorie secundara"
        field="parentCategoryIds"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <CategoryMultiselect
            departmentId={departmentId}
            level={1}
            restrictToIds={grandParentCategoryIds?.length ? availableCategoryIds : undefined}
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e)}
          />
        )}
        body={(rowData: Ticket) => <span>{rowData.parentCategory?.name}</span>}
      />
    ),
  },
  {
    header: "Categorie tertiara",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Categorie tertiara"
        field="categoryIds"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <CategoryMultiselect
            departmentId={departmentId}
            level={2}
            restrictToIds={grandParentCategoryIds?.length || parentCategoryIds?.length ? availableCategoryIds : undefined}
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e)}
          />
        )}
        body={(rowData: Ticket) => <span>{rowData.category?.name}</span>}
      />
    ),
  },
  {
    header: "Locatie",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(135)}
        header="Locatie"
        field="location"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <LocationsMultiSelect showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
        )}
        body={(rowData: Ticket) => <span>{rowData.location?.name}</span>}
      ></Column>
    ),
  },
  {
    header: "Grupa",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Grupa"
        field="teamId"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TeamsMultiSelect showClear departmentId={departmentId} value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
        )}
        body={(rowData: Ticket) => (
          <div className="flex">
            <span className="text-ellipsis" title={rowData.team?.name}>
              {rowData.team?.name}
            </span>
          </div>
        )}
      ></Column>
    ),
  },
  {
    header: "Agent",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Agent"
        field="agentId"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <UsersDropdown
            showClear
            noneOptionLabel="Nici un agent"
            teamIds={agentsFilterTeamId ? [agentsFilterTeamId] : undefined}
            role={UserRoleEnum.Agent}
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e)}
          />
        )}
        body={(rowData: Ticket) => (
          <div className="flex">
            <span className="text-ellipsis" title={rowData.agent?.displayName}>
              {rowData.agent?.firstName} {rowData.agent?.lastName}
            </span>
          </div>
        )}
      ></Column>
    ),
  },
  {
    header: "Utilizator",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Utilizator"
        field="ownerId"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <UsersDropdown showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
        )}
        body={(rowData: Ticket) => (
          <div className="flex">
            <span className="text-ellipsis" title={rowData.owner?.displayName}>
              {rowData.owner?.firstName} {rowData.owner?.lastName}
            </span>
          </div>
        )}
      ></Column>
    ),
  },
  {
    header: "Data crearii",
    column: (key: string) => (
      <Column
        key={key}
        style={fixedWidthColumnStyle(200)}
        header="Data crearii"
        field="createdDate"
        filter
        showFilterMatchModes={false}
        showFilterMenu={false}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <Calendar
            showButtonBar
            dateFormat="dd/mm/yy"
            value={convertDateStringToDate(options.value)}
            onChange={(e) => options.filterApplyCallback(e.value ? convertDateToDateString(e.value) : null)}
          />
        )}
        body={(rowData: Ticket) => <span>{rowData.createdPretty}</span>}
      ></Column>
    ),
  },
];

export const persistVisibleColumns = (visibleColumns: ColumnDef[]) => {
  localStorage.setItem(LS_AGENT_TICKETS_PAGE_VISIBLE_COLUMNS, JSON.stringify(visibleColumns.map((col) => col.header)));
};

export const fetchVisibleColumns = (columnsProps: ColumnDefProps, bypassStorage?: boolean) => {
  if (bypassStorage) {
    return columns(columnsProps);
  }

  const savedVisibleColumns: string[] = JSON.parse(localStorage.getItem(LS_AGENT_TICKETS_PAGE_VISIBLE_COLUMNS) || "[]");
  let result: ColumnDef[] = [];
  savedVisibleColumns.forEach((header) => {
    const found = columns(columnsProps).find((c) => c.header === header);
    if (found) {
      result.push(found);
    }
  });
  return result.length ? result : columns(columnsProps);
};

export const clearPersistedVisibleColumns = () => {
  localStorage.removeItem(LS_AGENT_TICKETS_PAGE_VISIBLE_COLUMNS);
};

export const persistFilters = (filters: any) => {
  localStorage.setItem(LS_AGENT_TICKETS_FILTERS, JSON.stringify(filters));
};

export const fetchFilters = (teamId?: string, bypassStorage?: boolean) => {
  const defaultFilters = {
    prettyId: { value: null, matchMode: FilterMatchMode.EQUALS },
    subject: { value: null, matchMode: FilterMatchMode.EQUALS },
    urgency: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: inWorkStatusesIds, matchMode: FilterMatchMode.EQUALS },
    teamId: { value: teamId ? [teamId] : [], matchMode: FilterMatchMode.EQUALS },
    agentId: { value: null, matchMode: FilterMatchMode.EQUALS },
    location: { value: [], matchMode: FilterMatchMode.EQUALS },
    grandParentCategoryIds: { value: [], matchMode: FilterMatchMode.EQUALS },
    parentCategoryIds: { value: [], matchMode: FilterMatchMode.EQUALS },
    categoryIds: { value: [], matchMode: FilterMatchMode.EQUALS },
    ownerId: { value: null, matchMode: FilterMatchMode.EQUALS },
    createdDate: { value: null, matchMode: FilterMatchMode.EQUALS },
  };
  if (bypassStorage) {
    return defaultFilters;
  }

  const filters = localStorage.getItem(LS_AGENT_TICKETS_FILTERS);
  return JSON.parse(filters || JSON.stringify(defaultFilters));
};

export const clearPersistedFilters = () => {
  localStorage.removeItem(LS_AGENT_TICKETS_FILTERS);
};
